import { useState, useEffect, useCallback, useRef } from 'react';
import { Text } from '@qwickapps/react-framework';
import { useAuth } from '@qwickapps/auth-client';
import { config } from '../../config/runtime';

type VMStatus = 'healthy' | 'warning' | 'critical' | 'unknown';

interface VM {
  id: string;
  name: string;
  status: VMStatus;
  cpu: number;
  memory: number;
  disk: number;
  agentCount?: number;
  loadAvg?: number;
  uptimeSeconds?: number;
  lastUpdated?: string;
  cpuHistory?: number[];
  memHistory?: number[];
}

interface InfrastructurePanelProps {
  vms?: VM[];
  loading?: boolean;
}

const STATIC_FALLBACK_VMS: VM[] = [
  { id: 'macmini-devserver', name: 'macmini-devserver', status: 'unknown', cpu: 0, memory: 0, disk: 0 },
  { id: 'oci-main', name: 'oci-main', status: 'unknown', cpu: 0, memory: 0, disk: 0 },
  { id: 'oci-dev', name: 'oci-dev', status: 'unknown', cpu: 0, memory: 0, disk: 0 },
  { id: 'oci-gateway', name: 'oci-gateway', status: 'unknown', cpu: 0, memory: 0, disk: 0 },
];

const REFRESH_INTERVAL_MS = 30_000;

function formatUptime(seconds: number): string {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

function formatRelativeTime(isoString: string): string {
  const diffSec = Math.floor((Date.now() - new Date(isoString).getTime()) / 1000);
  if (diffSec < 5) return 'Updated just now';
  if (diffSec < 60) return `Updated ${diffSec}s ago`;
  if (diffSec < 3600) return `Updated ${Math.floor(diffSec / 60)}m ago`;
  return `Updated ${Math.floor(diffSec / 3600)}h ago`;
}

function Sparkline({ values, color }: { values: number[]; color: string }) {
  const max = 100;
  const w = 80;
  const h = 24;
  if (!values || values.length < 2) return null;
  const pts = values
    .map((v, i) => {
      const x = (i / (values.length - 1)) * w;
      const y = h - (v / max) * h;
      return `${x},${y}`;
    })
    .join(' ');
  return (
    <svg width={w} height={h} style={{ display: 'block' }}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth={1.5} />
    </svg>
  );
}

export default function InfrastructurePanel({ vms: vmsProp, loading: loadingProp = false }: InfrastructurePanelProps) {
  const isSelfManaged = vmsProp === undefined;
  const [vms, setVms] = useState<VM[]>(vmsProp ?? STATIC_FALLBACK_VMS);
  const [isFallback, setIsFallback] = useState(isSelfManaged);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { session } = useAuth();

  const fetchVMs = useCallback(
    async (showRefreshing = false) => {
      if (!isSelfManaged) return;
      if (showRefreshing) setIsRefreshing(true);
      try {
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (session?.accessToken) {
          headers['Authorization'] = `Bearer ${session.accessToken}`;
        }
        const res = await fetch(`${config.apiUrl}/api/infrastructure/vms`, { headers });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const liveVMs: VM[] = Array.isArray(data) ? data : (data.vms ?? []);
        if (liveVMs.length > 0) {
          setVms(liveVMs);
          setIsFallback(false);
        }
      } catch {
        setIsFallback(true);
      } finally {
        if (showRefreshing) setIsRefreshing(false);
      }
    },
    [isSelfManaged, session?.accessToken],
  );

  useEffect(() => {
    if (!isSelfManaged) return;
    fetchVMs(false);
    const interval = setInterval(() => fetchVMs(true), REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [isSelfManaged, fetchVMs]);

  useEffect(() => {
    if (!isSelfManaged && vmsProp !== undefined) {
      setVms(vmsProp);
      setIsFallback(false);
    }
  }, [vmsProp, isSelfManaged]);

  if (loadingProp) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '64px 0' }}>
        <div role="progressbar" aria-label="loading" style={{
          width: 24,
          height: 24,
          border: '2px solid var(--theme-border-color)',
          borderTopColor: 'var(--theme-color-primary)',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
        }} />
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <Text variant="h5" content="Infrastructure" gutterBottom />
        {isRefreshing && (
          <div
            role="status"
            aria-label="refreshing"
            style={{
              width: 12,
              height: 12,
              border: '2px solid var(--theme-border-color)',
              borderTopColor: 'var(--theme-color-primary)',
              borderRadius: '50%',
              animation: 'spin 0.8s linear infinite',
            }}
          />
        )}
      </div>
      <Text
        variant="body2"
        content="Monitor your VMs and containers"
        customColor="var(--theme-text-secondary)"
        gutterBottom
      />

      {isFallback && (
        <div
          style={{
            marginBottom: 16,
            padding: 12,
            borderRadius: 4,
            background: 'var(--theme-bg-surface)',
            border: '1px dashed var(--theme-border-color)',
          }}
        >
          <Text
            variant="caption"
            content="Infrastructure monitoring unavailable — connect Beszel to enable live metrics"
            customColor="var(--theme-text-secondary)"
          />
        </div>
      )}

      {vms.length === 0 ? (
        <div
          style={{
            padding: 32,
            borderRadius: 8,
            background: 'var(--theme-bg-surface)',
            border: '1px solid var(--theme-border-color)',
            textAlign: 'center',
          }}
        >
          <Text
            variant="body1"
            content="No infrastructure data available"
            customColor="var(--theme-text-secondary)"
          />
          <Text
            variant="caption"
            content="Connect Beszel to enable infrastructure monitoring"
            customColor="var(--theme-text-disabled)"
          />
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16, marginTop: 16 }}>
          {vms.map(vm => (
            <VMCard key={vm.id} vm={vm} />
          ))}
        </div>
      )}
    </div>
  );
}

function VMCard({ vm }: { vm: VM }) {
  const statusColors: Record<VMStatus, string> = {
    healthy: '#4caf50',
    warning: '#ff9800',
    critical: '#f44336',
    unknown: '#9e9e9e',
  };

  const agentLabel =
    vm.agentCount === undefined
      ? null
      : vm.agentCount === 0
        ? 'No agents'
        : `${vm.agentCount} agent${vm.agentCount === 1 ? '' : 's'}`;

  const uptimeLabel = vm.uptimeSeconds !== undefined ? formatUptime(vm.uptimeSeconds) : null;
  const lastUpdatedLabel = vm.lastUpdated ? formatRelativeTime(vm.lastUpdated) : null;

  return (
    <div
      style={{
        padding: 16,
        borderRadius: 8,
        background: 'var(--theme-bg-surface)',
        border: '1px solid var(--theme-border-color)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div
            style={{
              width: 10,
              height: 10,
              borderRadius: '50%',
              background: statusColors[vm.status],
              flexShrink: 0,
            }}
          />
          <Text variant="subtitle1" content={vm.name} />
        </div>
        {agentLabel !== null && (
          <span
            style={{
              fontSize: 11,
              padding: '2px 6px',
              borderRadius: 10,
              background: vm.agentCount ? 'var(--theme-color-primary)' : 'var(--theme-border-color)',
              color: vm.agentCount ? '#fff' : 'var(--theme-text-secondary)',
            }}
          >
            {agentLabel}
          </span>
        )}
      </div>

      <MetricBar label="CPU" value={vm.cpu} color="#2196f3" history={vm.cpuHistory} />
      <MetricBar label="Memory" value={vm.memory} color="#9c27b0" history={vm.memHistory} />
      <MetricBar label="Disk" value={vm.disk} color="#ff9800" />

      <div
        style={{
          marginTop: 12,
          display: 'flex',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 4,
        }}
      >
        {vm.loadAvg !== undefined && (
          <Text
            variant="caption"
            content={`Load: ${vm.loadAvg.toFixed(2)}`}
            customColor="var(--theme-text-secondary)"
          />
        )}
        {uptimeLabel && (
          <Text
            variant="caption"
            content={`Up: ${uptimeLabel}`}
            customColor="var(--theme-text-secondary)"
          />
        )}
        {lastUpdatedLabel && (
          <Text
            variant="caption"
            content={lastUpdatedLabel}
            customColor="var(--theme-text-disabled)"
          />
        )}
      </div>
    </div>
  );
}

function MetricBar({
  label,
  value,
  color,
  history,
}: {
  label: string;
  value: number;
  color: string;
  history?: number[];
}) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Text variant="caption" content={label} customColor="var(--theme-text-secondary)" />
          {history && history.length >= 2 && <Sparkline values={history} color={color} />}
        </div>
        <Text variant="caption" content={`${value}%`} />
      </div>
      <div
        style={{
          height: 6,
          borderRadius: 3,
          background: 'var(--theme-border-color)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            height: '100%',
            width: `${value}%`,
            background: color,
            borderRadius: 3,
            transition: 'width 0.3s ease',
          }}
        />
      </div>
    </div>
  );
}
